/**
 * LiveEscaleta — Modo en Vivo de la Escaleta (P6).
 *
 * Checklist en cabina: el DJ va marcando cada momento como HECHO conforme
 * avanza la noche. Resalta el momento ACTUAL (primero sin hacer), muestra
 * progreso y permite reproducir el MP3 / abrir Spotify/YouTube de las
 * canciones enlazadas a peticiones (reutiliza los datos de peticiones).
 */
import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ListOrdered, Clock, MapPin, Music, Play, Pause, Check, CircleDot,
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { API_URL } from '../config';

const normCancion = (c) => (typeof c === 'string' ? { titulo: c } : (c || {}));

const LiveEscaleta = ({ playlist, token }) => {
  const peticiones = playlist?.peticiones || [];
  const [blocks, setBlocks] = useState(
    (playlist?.escaleta || []).map((b) => ({ ...b, done: !!b.done })),
  );
  const [savingId, setSavingId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const doneCount = useMemo(() => blocks.filter((b) => b.done).length, [blocks]);
  const total = blocks.length;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;
  // Momento "actual": el primero sin hacer
  const currentIdx = blocks.findIndex((b) => !b.done);

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });

  const playMp3 = async (fileId) => {
    if (playingId === fileId && audioRef.current) {
      audioRef.current.pause(); setPlayingId(null); return;
    }
    try {
      if (audioRef.current) audioRef.current.pause();
      const res = await axios.get(`${API_URL}/audio/${fileId}`, { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => setPlayingId(null);
      await a.play();
      setPlayingId(fileId);
    } catch {
      toast.error('No se pudo reproducir el MP3');
    }
  };

  const toggleDone = async (block) => {
    const next = !block.done;
    setBlocks((prev) => prev.map((b) => (b.id === block.id ? { ...b, done: next } : b)));
    setSavingId(block.id);
    try {
      await axios.patch(
        `${API_URL}/playlists/${playlist.id}/escaleta/${block.id}/done`,
        { done: next },
        { headers: authHeaders() },
      );
    } catch (e) {
      // revertir si falla
      setBlocks((prev) => prev.map((b) => (b.id === block.id ? { ...b, done: !next } : b)));
      toast.error('No se pudo guardar el cambio');
    } finally {
      setSavingId(null);
    }
  };

  if (total === 0) {
    return (
      <div className="text-center py-12 text-slate-400 border border-dashed border-slate-700 rounded-lg" data-testid="live-escaleta-empty">
        <ListOrdered className="w-10 h-10 mx-auto mb-2 text-slate-600" />
        Este evento no tiene escaleta.
        <p className="text-slate-500 text-sm mt-1">Genera y guarda la escaleta desde la ficha del evento.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="live-escaleta">
      {/* Progreso */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white font-semibold flex items-center gap-2">
            <ListOrdered className="w-5 h-5 text-sky-400" /> Escaleta en vivo
          </span>
          <span className="text-sm text-slate-300" data-testid="live-escaleta-progress">
            {doneCount}/{total} momentos
          </span>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} data-testid="live-escaleta-bar" />
        </div>
      </div>

      {/* Bloques */}
      <div className="space-y-2">
        {blocks.map((b, idx) => {
          const isCurrent = idx === currentIdx;
          const canciones = (b.canciones || []).map(normCancion);
          return (
            <Card
              key={b.id}
              className={`border ${b.done ? 'bg-slate-900/40 border-slate-800 opacity-70'
                : isCurrent ? 'bg-sky-950/40 border-sky-500 shadow-lg shadow-sky-500/10'
                  : 'bg-slate-800 border-slate-700'}`}
              data-testid={`live-escaleta-block-${idx}`}
            >
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  {/* Toggle hecho */}
                  <button
                    type="button"
                    onClick={() => toggleDone(b)}
                    disabled={savingId === b.id}
                    className={`mt-0.5 w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                      b.done ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                    title={b.done ? 'Marcar como pendiente' : 'Marcar como hecho'}
                    data-testid={`live-escaleta-toggle-${idx}`}
                  >
                    <Check className="w-5 h-5" />
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {b.hora && (
                        <span className="text-white font-bold flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />{b.hora}
                        </span>
                      )}
                      <span className={`font-semibold ${b.done ? 'line-through text-slate-500' : 'text-white'}`}>
                        {b.titulo || 'Momento'}
                      </span>
                      {isCurrent && !b.done && (
                        <Badge className="bg-sky-600 text-white text-[10px]" data-testid={`live-escaleta-now-${idx}`}>
                          <CircleDot className="w-3 h-3 mr-1 animate-pulse" /> AHORA
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5 text-xs">
                      {b.fase && b.fase !== b.titulo && <span className="text-slate-400">{b.fase}</span>}
                      {b.espacio && (
                        <span className="text-cyan-400 flex items-center gap-0.5">
                          <MapPin className="w-3 h-3" />{b.espacio}
                        </span>
                      )}
                    </div>

                    {/* Canciones */}
                    {canciones.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {canciones.map((c, ci) => {
                          const pet = c.peticion_id ? peticiones.find((p) => p.id === c.peticion_id) : null;
                          const isPlaying = pet && playingId && playingId === pet.audio_file_id;
                          return (
                            <div key={ci} className="flex items-center gap-2 bg-slate-800/70 rounded px-2 py-1 text-sm" data-testid={`live-escaleta-song-${idx}-${ci}`}>
                              <Music className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                              <span className="text-slate-200 truncate flex-1 min-w-0">
                                {c.titulo}{(pet?.artista || c.artista) ? <span className="text-slate-400"> — {pet?.artista || c.artista}</span> : null}
                                {(c.nota || pet?.nota) ? <span className="text-amber-300/90 italic"> ({c.nota || pet?.nota})</span> : null}
                                {!pet && <span className="text-[10px] text-slate-500 italic ml-1">(sugerencia)</span>}
                              </span>
                              {pet?.audio_file_id && (
                                <button type="button" onClick={() => playMp3(pet.audio_file_id)} className="text-green-400 hover:text-green-300 shrink-0" title="Reproducir MP3" data-testid={`live-escaleta-play-${idx}-${ci}`}>
                                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                </button>
                              )}
                              {pet?.spotify_url && (
                                <a href={pet.spotify_url} target="_blank" rel="noreferrer" className="text-[10px] font-semibold text-green-400 border border-green-500/40 rounded px-1.5 py-0.5 hover:bg-green-500/10 shrink-0">Spotify</a>
                              )}
                              {pet?.youtube_url && (
                                <a href={pet.youtube_url} target="_blank" rel="noreferrer" className="text-[10px] font-semibold text-red-400 border border-red-500/40 rounded px-1.5 py-0.5 hover:bg-red-500/10 shrink-0">YT</a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {b.nota && <p className="mt-1.5 text-xs text-amber-300/90 italic">{b.nota}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default LiveEscaleta;
export { LiveEscaleta };
